import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { DatePipe } from '@angular/common';
import {
  formatCredits,
  isLowCreditBalance,
  consumedFraction,
} from '@kirigami/domain/analysis-rules';

export type CreditMeterState = 'normal' | 'low' | 'exhausted' | 'no-plan';

/**
 * Host-agnostic credit meter. Renders a subdued "M used" figure on the LEFT
 * and the "N credits left" headline on the RIGHT, over a bar that FILLS as
 * credits are spent (consumed fraction — usage-progress reading, matching
 * the "used" figure it sits under), plus the reset date. Emits `upgrade`
 * when the user clicks the upgrade CTA (shown when exhausted / no plan).
 *
 * All values are integer credits provided by the API (master plan C1/C7: the
 * backend is the single credit authority) — the component never converts
 * tokens. `available` IS the backend-floored spendable figure and the number
 * the headline leads with: it can legitimately exceed `allowance - consumed`
 * (personal-wallet top-ups add to a monthly plan's spendable), which is why
 * the copy never derives "left" from the allowance. `used` is displayed
 * verbatim beside it, never combined with the other figures arithmetically.
 * In wallet mode the caller pre-synthesizes `allowance` as
 * available + consumed (resolveActiveUsage), so the bar's 100% keeps meaning.
 */
@Component({
  selector: 'kirigami-credit-meter',
  standalone: true,
  imports: [DatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './credit-meter.html',
  styleUrls: ['./credit-meter.scss'],
})
export class CreditMeterComponent {
  /** Display allowance in credits — the bar's 100% (wallet mode: available + consumed, pre-synthesized). */
  @Input() allowance = 0;
  /** Spendable credits (integer, backend floor). */
  @Input() available = 0;
  /** Credits consumed this period (integer) — the subdued secondary figure. */
  @Input() used = 0;
  /** Picks the low-balance rule: 10% of allowance (monthly) vs the fixed wallet floor. */
  @Input() mode: 'monthly' | 'wallet' = 'monthly';
  @Input() resetDate: string | null = null;
  /**
   * Whether the user actually has an active plan. "No active plan" must be
   * an explicit caller decision — a plan whose figures are 0 (exhausted
   * allowance, or a mis-provisioned pool) is NOT the same as having no plan,
   * and inferring it from allowance==0 mislabeled real subscriptions.
   */
  @Input() hasPlan = true;
  /**
   * Optional state label that REPLACES the meter entirely (headline +
   * upgrade CTA, no bar) — e.g. "Free plan previously used" when the
   * one-time Free benefit belonged to a previous account and a 0/0 bar
   * would wrongly imply a fresh allowance. Presentational only; the caller
   * decides when it applies.
   */
  @Input() noticeLabel: string | null = null;
  /**
   * Optional ADDITIVE line rendered inside the normal (bar) branch, after the
   * bar and next to `resetDate` — e.g. "10 credits reserved while your
   * analysis runs". Unlike `noticeLabel`, this never replaces the meter or
   * adds an Upgrade CTA; it is purely informational and stays subdued. Null
   * (default) renders nothing extra. When `noticeLabel` is set, it wins and
   * this is not rendered — `noticeLabel`'s replacement semantics are
   * untouched by this input.
   */
  @Input() reserveNote: string | null = null;
  /**
   * Optional ADDITIVE line for the personal-wallet balance (2026-08-04
   * headline split: `available` is the plan's OWN pool, so wallet credits
   * spendable on top of it surface here — e.g. "Plus 1,251 credits in your
   * personal wallet" — instead of silently inflating the headline). Rendered
   * in the bar branch AND in the exhausted state (an empty plan pool with a
   * non-empty wallet still spends), always subdued. Null renders nothing.
   */
  @Input() walletNote: string | null = null;
  /**
   * Plain-language explanation of the personal wallet, rendered as a
   * hover/focus tooltip behind a small (i) icon next to `walletNote` — the
   * bare figure confused users ("where do these extra credits come from?").
   * The icon renders only when BOTH walletNote and this detail are set; the
   * text is also the icon's aria-label so screen readers get the full
   * explanation. Null renders the note without an icon.
   */
  @Input() walletNoteDetail: string | null = null;

  @Output() upgrade = new EventEmitter<void>();

  get fraction(): number {
    return consumedFraction(this.allowance, this.used);
  }

  /** Consumed fraction as an integer 0..100, for the bar width and ARIA value. */
  get fillPercentValue(): number {
    return Math.round(this.fraction * 100);
  }

  /** Bar width as an integer percentage string for the template. */
  get fillPercent(): string {
    return `${this.fillPercentValue}%`;
  }

  get remainingLabel(): string {
    return formatCredits(this.available);
  }

  get usedLabel(): string {
    return formatCredits(this.used);
  }

  get state(): CreditMeterState {
    if (!this.hasPlan) return 'no-plan';
    if (this.available <= 0) return 'exhausted';
    if (isLowCreditBalance(this.available, this.mode === 'monthly' ? this.allowance : 0)) {
      return 'low';
    }
    return 'normal';
  }

  onUpgrade(): void {
    this.upgrade.emit();
  }
}
